// assets/duftstecker-konfigurator.js
// Live-Gravur für den Duftstecker: freier Text + Inline-Symbole (an Cursor-Position),
// stilisierte Live-Vorschau, Umfang-Variante (Einzeln/Spar-Paket).
// Reine Funktionen sind DOM-frei und werden mit node --test geprüft.

// Kürzt einen Text auf max Zeichen, toleriert null/undefined.
export function clampText(value, max) {
  return String(value ?? "").slice(0, max);
}

// Fügt symbol an der Auswahl [start, end) ein; ersetzt eine Selektion.
// Ungültige/fehlende Indizes landen am Textende. Gibt neuen Text + Caret zurück.
export function insertSymbol(text, symbol, start, end) {
  const base = String(text ?? "");
  const sym = String(symbol ?? "");
  const len = base.length;
  const clamp = (n) => {
    const num = Number(n);
    return Number.isInteger(num) ? Math.min(len, Math.max(0, num)) : len;
  };
  let s = clamp(start);
  let e = clamp(end);
  if (s > e) [s, e] = [e, s];
  return { text: base.slice(0, s) + sym + base.slice(e), caret: s + sym.length };
}

// Baut die Line-Item-Property (leerer Text => keine Property). Exakter Key: "Gravur".
export function buildProperties({ gravur = "" } = {}) {
  const props = {};
  if (gravur) props["Gravur"] = gravur;
  return props;
}

// Findet die Variante mit exakt gleicher Options-Sequenz oder null.
export function selectVariant(variants, selection) {
  return variants.find(v =>
    v.options.length === selection.length &&
    v.options.every((opt, i) => opt === selection[i])
  ) ?? null;
}

// Liefert die Umfang-Werte (Option 2), für die eine Variante mit Duft (Option 1)
// = duft existiert. Dedupliziert; "+ beide Nachfüllsets" steht immer zuletzt, die
// übrigen behalten ihre Reihenfolge (stabiler Sort).
export function validUmfangValues(variants, duft) {
  const seen = new Set();
  const result = [];
  for (const v of variants) {
    if (v.options[0] !== duft) continue;
    const umfang = v.options[1];
    if (!seen.has(umfang)) { seen.add(umfang); result.push(umfang); }
  }
  return result.sort((a, b) => (a.includes("beide") ? 1 : 0) - (b.includes("beide") ? 1 : 0));
}

// Formatiert Cent-Beträge deutsch: 1999 => "19,99 €".
export function formatEuro(cents) {
  return (cents / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

export function buildPriceState(price, compareAt) {
  const current = Number(price) || 0;
  const compareValue = Number(compareAt) || 0;
  const onSale = compareValue > current;
  return {
    current: formatEuro(current),
    compare: onSale ? formatEuro(compareValue) : "",
    percent: onSale ? Math.round(((compareValue - current) * 100) / compareValue) : 0,
    onSale
  };
}

// Formatiert eine Schriftgröße in ganze Prozent: 140 => "140 %".
export function formatGroesse(value) {
  const n = Number(value);
  return (Number.isFinite(n) ? Math.round(n) : 0) + " %";
}

export function fitScale(contentWidth, availableWidth) {
  const content = Number(contentWidth);
  const available = Number(availableWidth);
  if (!Number.isFinite(content) || content <= 0 || !Number.isFinite(available) || available <= 0) return 1;
  return Math.max(0.1, Math.min(1, available / content));
}

// Nächstliegender Slide-Index aus der Track-Scrollposition, geklemmt auf [0, count-1].
// Ungültige/nicht-positive slideWidth oder count ⇒ 0.
export function activeSlideIndex(scrollLeft, slideWidth, count) {
  const total = Number.isInteger(count) && count > 0 ? count : 0;
  if (total === 0) return 0;
  const w = Number(slideWidth);
  if (!Number.isFinite(w) || w <= 0) return 0;
  const raw = Math.round(Number(scrollLeft) / w);
  return Math.min(total - 1, Math.max(0, Number.isFinite(raw) ? raw : 0));
}

// ── DOM-Wiring ──────────────────────────────────────────────────────────────
function initDuftstecker(root) {
  const gravurInput = root.querySelector("[data-gravur-input]");
  const variantsEl = root.querySelector("[data-variants]");
  if (!gravurInput || !variantsEl) return;

  const variants = JSON.parse(variantsEl.textContent)
    .map(v => ({ id: v.id, options: v.options, available: v.available, price: v.price, compareAt: v.compare_at_price }));
  if (!variants.length) return;

  const symbolBtns = Array.from(root.querySelectorAll("[data-symbol]"));
  // Bühnen-Overlay + mobile Inline-Vorschau im Gravur-Panel werden gemeinsam gepflegt.
  const overlays = Array.from(root.querySelectorAll("[data-duft-preview-text]"));
  const idField = root.querySelector("[data-variant-id]");
  const addBtn = root.querySelector("[data-add-to-cart]");
  const priceDisplay = root.querySelector("[data-price-display]");
  const sizeSlider = root.querySelector("[data-gravur-size]");
  const groesseField = root.querySelector("[data-gravur-groesse]");
  const MAX = Number(gravurInput.getAttribute("maxlength")) || 30;

  // Optionsindizes dynamisch aus dem DOM (Duftstecker: nur Umfang = 0).
  const optionIndices = [...new Set(
    Array.from(root.querySelectorAll("[data-option-index]"))
      .map(el => Number(el.dataset.optionIndex))
  )].sort((a, b) => a - b);

  // Duft-Auswahl (Achse 0) + abhängiger Umfang-Dropdown (Achse 1) + Info-Umschaltung.
  const umfangSelect = root.querySelector('[data-option-index="1"]');
  const duftBeschreibungen = Array.from(root.querySelectorAll("[data-duft-beschreibung]"));
  const duftCaption = root.querySelector("[data-duft-caption]");

  function currentDuft() {
    return root.querySelector("[data-duft-option]:checked")?.value ?? variants[0].options[0];
  }

  // Umfang-Dropdown neu aufbauen: nur die zum aktiven Duft existierenden Optionen.
  // Ungültige (der bereits enthaltene Duft) werden entfernt statt versteckt, damit
  // sie auch in Safari nicht auftauchen. "+ beide Nachfüllsets" steht immer unten.
  function syncUmfangOptions() {
    if (!umfangSelect) return;
    const duft = currentDuft();
    const values = validUmfangValues(variants, duft);
    const previous = umfangSelect.value;
    umfangSelect.innerHTML = "";
    values.forEach(value => {
      const variant = selectVariant(variants, [duft, value]);
      const opt = document.createElement("option");
      opt.value = value;
      opt.textContent = variant ? `${value} · ${formatEuro(variant.price)}` : value;
      umfangSelect.appendChild(opt);
    });
    umfangSelect.value = values.includes(previous) ? previous : (values[0] ?? "");
  }

  // Beschreibung + Bildunterschrift auf den aktiven Duft schalten.
  function syncDuftInfo() {
    const duft = currentDuft();
    duftBeschreibungen.forEach(el => { el.hidden = el.dataset.duftName !== duft; });
    if (duftCaption) duftCaption.textContent = "Duft: " + duft;
  }

  // Duft-abhängige DOM-Arbeit (Select neu bauen, Beschreibung/Caption schalten)
  // nur bei echtem Duft-Wechsel. Sonst würde jeder Gravur-Tastendruck den
  // <select> neu aufbauen, was auf iOS den Fokus zurück ins Eingabefeld scrollt.
  let lastDuft = null;
  function syncDuftDependent() {
    const duft = currentDuft();
    if (duft === lastDuft) return;
    lastDuft = duft;
    syncUmfangOptions();
    syncDuftInfo();
  }

  function currentSelection() {
    if (!optionIndices.length) return variants[0].options.slice();
    return optionIndices.map(i => {
      const el = root.querySelector(`[data-option-index="${i}"]`);
      if (el && el.tagName === "SELECT") return el.value;
      return root.querySelector(`[data-option-index="${i}"]:checked`)?.value ?? variants[0].options[i];
    });
  }

  function syncPreview() {
    const value = clampText(gravurInput.value, MAX);
    overlays.forEach(o => {
      o.textContent = value || o.dataset.placeholder || "";
    });
    fitOverlays();
  }

  function fitOverlays() {
    const baseSize = Number(sizeSlider?.value) || 100;
    let effectiveSize = baseSize;
    overlays.forEach(o => {
      o.style.setProperty("--gravur-fit", "1");
      const availableWidth = (o.parentElement?.clientWidth || 0) * 0.88;
      const scale = fitScale(o.scrollWidth, availableWidth);
      o.style.setProperty("--gravur-fit", String(scale));
      if (availableWidth > 0) effectiveSize = Math.min(effectiveSize, baseSize * scale);
    });
    if (groesseField) groesseField.value = formatGroesse(effectiveSize);
  }

  // Schriftgröße: Overlay-Variable + versteckte Property setzen.
  function applyGroesse() {
    if (!sizeSlider) return;
    overlays.forEach(o => o.style.setProperty("--gravur-size", sizeSlider.value));
    fitOverlays();
  }

  function sync() {
    syncDuftDependent();
    // Nur zuweisen, wenn wirklich gekürzt werden muss. Das Feld hat maxlength,
    // also greift das faktisch nie beim Tippen. Eine .value-Zuweisung bei jedem
    // Tastendruck lässt iOS Safari den Fokus zurück ins Feld scrollen.
    if (gravurInput.value.length > MAX) gravurInput.value = clampText(gravurInput.value, MAX);
    syncPreview();
    // Gravur-Größe nur mitsenden, wenn ein Gravur-Text existiert — sonst zeigt der
    // Warenkorb einen einsamen Größenwert ohne Text. Deaktivierte Felder werden
    // nicht submittet.
    if (groesseField) groesseField.disabled = gravurInput.value.trim() === "";
    const variant = selectVariant(variants, currentSelection());
    if (!variant) return;
    if (idField) idField.value = variant.id;
    const priceState = buildPriceState(variant.price, variant.compareAt);
    const currentPrice = priceDisplay?.querySelector("[data-price-current]");
    const comparePrice = priceDisplay?.querySelector("[data-price-compare]");
    const priceBadge = priceDisplay?.querySelector("[data-price-badge]");
    if (currentPrice) currentPrice.textContent = priceState.current;
    if (comparePrice) {
      comparePrice.textContent = priceState.compare;
      comparePrice.hidden = !priceState.onSale;
    }
    if (priceBadge) {
      priceBadge.textContent = priceState.onSale ? `-${priceState.percent}%` : "";
      priceBadge.hidden = !priceState.onSale;
    }
    if (addBtn) {
      addBtn.disabled = !variant.available;
      addBtn.textContent = variant.available
        ? `In den Warenkorb · ${formatEuro(variant.price)}`
        : "Ausverkauft";
    }
  }

  // Symbol an der Cursor-/Selektionsstelle einfügen (nicht am Ende).
  symbolBtns.forEach(btn => {
    btn.addEventListener("click", () => {
      const { text, caret } = insertSymbol(
        gravurInput.value, btn.dataset.symbol || "",
        gravurInput.selectionStart, gravurInput.selectionEnd
      );
      gravurInput.value = clampText(text, MAX);
      const safeCaret = Math.min(caret, gravurInput.value.length);
      gravurInput.focus();
      gravurInput.setSelectionRange(safeCaret, safeCaret);
      syncPreview();
    });
  });

  // ── Carousel: Track, Punkte, Thumbnails ─────────────────────────────────
  const track = root.querySelector("[data-gallery-track]");
  const slides = track ? Array.from(track.querySelectorAll(".gallery-slide")) : [];
  const dots = Array.from(root.querySelectorAll("[data-dot]"));
  const photoThumbs = Array.from(root.querySelectorAll("[data-duft-thumb]"));

  // Aktiven Punkt + Thumbnail markieren.
  function markActive(index) {
    dots.forEach((d, i) => {
      const on = i === index;
      d.classList.toggle("gallery-dot--active", on);
      d.setAttribute("aria-pressed", on ? "true" : "false");
    });
    photoThumbs.forEach((t, i) => {
      const on = i === index;
      t.classList.toggle("product-thumbnail--active", on);
      t.setAttribute("aria-pressed", on ? "true" : "false");
    });
  }

  // Track zum Slide scrollen (Slide-Breite = Track-Breite, flex 0 0 100%).
  function goToSlide(index) {
    if (!track || !slides.length) return;
    const i = Math.min(slides.length - 1, Math.max(0, index));
    track.scrollTo({ left: i * track.clientWidth, behavior: "smooth" });
    markActive(i);
  }

  // Scrollposition → aktiven Index über die Pure Function, per rAF entprellt.
  let scrollRaf = 0;
  if (track) {
    track.addEventListener("scroll", () => {
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(() => {
        scrollRaf = 0;
        markActive(activeSlideIndex(track.scrollLeft, track.clientWidth, slides.length));
      });
    });
  }

  // ── Personalisieren: Panel aufklappen + Foto-Vorschau einblenden ────────
  const personalizeView = root.querySelector("[data-personalize-view]");
  const gravurPanel = root.querySelector("[data-gravur-panel]");
  const personalizeBtn = root.querySelector("[data-personalize-toggle]");
  let personalizing = false;

  function setPersonalize(on) {
    personalizing = on;
    if (personalizeView) personalizeView.hidden = !on;
    if (gravurPanel) gravurPanel.hidden = !on;
    if (personalizeBtn) {
      personalizeBtn.textContent = on
        ? (personalizeBtn.dataset.labelOn || "Vorschau schließen")
        : (personalizeBtn.dataset.labelOff || "Personalisieren");
      personalizeBtn.setAttribute("aria-pressed", on ? "true" : "false");
      personalizeBtn.setAttribute("aria-expanded", on ? "true" : "false");
    }
    if (on) {
      syncPreview();
      if (gravurInput) gravurInput.focus();
    }
  }
  if (personalizeBtn) {
    personalizeBtn.addEventListener("click", () => setPersonalize(!personalizing));
  }

  // Thumbnail/Punkt: zeigt das Foto und verlässt den Personalisieren-Modus.
  [...photoThumbs, ...dots].forEach(btn => {
    btn.addEventListener("click", () => {
      const index = Number(btn.dataset.index) || 0;
      setPersonalize(false);
      goToSlide(index);
    });
  });

  root.addEventListener("input", sync);
  root.addEventListener("change", sync);
  if (sizeSlider) sizeSlider.addEventListener("input", applyGroesse);
  markActive(0);
  sync();
  applyGroesse();
}

if (typeof document !== "undefined") {
  document.querySelectorAll("[data-duftstecker-configurator]").forEach(initDuftstecker);
}
